<template>
    <slot
        :type="attachmentType"
        :sourceIcon="sourceIcon"
        :typeIcon="iconType === 'bare' ? bareIcon : iconSrc('bare')"
        :tooltip="tooltip"
        :title="title"
        :url="url"
    />
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
    attachment: {
        type: [Object, Array],
        required: true
    },
    iconType: {
        type: String,
        default: 'bare'
    },
    meeting: {
        type: Object
    }
});

const config = useRuntimeConfig();
const route = useRoute();
const publicSiteData = useState('publicSiteData');

// Helper methods
const fromFileName = (name) => {
    return name.split('.').pop().toLowerCase();
};

const fileExt = () => {
    if (attachmentType.value === 'upload') {
        return props.attachment.file
            ? fromFileName(props.attachment.file.name)
            : fromFileName(props.attachment.name);
    }
    if (props.attachment.attachment) {
        let att = props.attachment.attachment;
        return isThirdParty.value
            ? fromFileName(att.name || att.attached_name)
            : att.ext ?? null;
    }
    if (props.attachment.document) {
        return props.attachment.document.fileExtension;
    }
    if (attachmentType.value === "computer") {
        return props.attachment.file_ext ?? fromFileName(props.attachment.filename);
    }
    if (isThirdParty.value) {
        return fromFileName(props.attachment.attached_name);
    }
};

const iconSrc = (type) => {
    let path = type === 'bare' ? '/img/icons/bare-' : '/img/icons/';
    const ext = fileExt();
    const map = {
        jpg: "jpg.svg",
        jpeg: "jpg.svg",
        xls: "ms_excel.svg",
        xlsx: "ms_excel.svg",
        doc: "ms_word.svg",
        docx: "ms_word.svg",
        msword: "ms_word.svg",
        ppt: "ms_powerpoint.svg",
        pptx: "ms_powerpoint.svg",
        png: "png.svg",
        pdf: "pdf.svg",
        mov: "mov.svg",
        mp4: "mp4.svg"
    };
    return `${path}${map[ext] || 'file.svg'}`;
};

// Computed properties
const attachmentType = computed(() => {
    let type = null;
    if (props.attachment.attachment || props.attachment.type) {
        type = props.attachment.type;
    } else if (props.attachment.attached_type) {
        type = props.attachment.attached_type;
    }
    return type && type.length ? type : "computer";
});

const isThirdParty = computed(() => {
    return ["dropbox", "google", "onedrive"].includes(attachmentType.value);
});

const sourceIcon = computed(() => {
    const map = {
        computer: "paperclip",
        upload: "paperclip",
        google: "brands-google-drive",
        dropbox: "brands-dropbox",
        onedrive: "solid-clouds",
        document: "boardable-mark"
    };
    return map[attachmentType.value];
});

const tooltip = computed(() => {
    const map = {
        computer: "Uploaded file",
        google: "Linked from Google Drive",
        dropbox: "Linked from Dropbox",
        onedrive: "Linked from OneDrive",
        document: "Linked from Document Center"
    };
    return map[attachmentType.value];
});

const bareIcon = computed(() => {
    let icon = null;
    if (props.attachment.document) {
        icon = props.attachment.document.bareIconUrl ?? null;
    } else if (props.attachment.attachment) {
        icon = props.attachment.attachment.bareIconUrl ?? null;
    } else if (attachmentType.value === 'computer') {
        icon = props.attachment.bareIconUrl ?? null;
    } else if (attachmentType.value === 'google') {
        icon = props.attachment.iconUrl ?? null;
    }
    return icon ?? iconSrc('bare');
});

const title = computed(() => {
    if (attachmentType.value === 'upload') {
        return props.attachment.file ? props.attachment.file.name : props.attachment.name;
    }
    if (props.attachment.attachment) {
        let att = props.attachment.attachment;
        return isThirdParty.value ? (att.name || att.attached_name) : att.title ?? null;
    }
    return props.attachment.title;
});

const url = computed(() => {
    if (!props.attachment?.uuid?.short_slug) return null;

    if ( ! process.client)
        return '';

    let url = config.public.boardDomain + '/';
    if (publicSiteData?.value)
        url += publicSiteData.value.organization_slug;
    else {
        const { org } = useOrg();
        url += org.value.slug;
    }
    if (props.meeting) {
        url += '/public-site-data/meetings/' + props.meeting.slug;
    }
    else {
        url += '/public-site-data/meetings/' + route.params.meeting_slug;
    }
    url += '/documents/' + props.attachment.uuid.short_slug + '/view';

    let pathArray = window.location.href.split("/");
    return url + "?redirect=" + pathArray[pathArray.length - 1];
});
</script>